// ManagePermissions page: allows Shipowner to grant or revoke supplier and verifier permissions for vessels.
import React, { useContext, useState, useEffect } from 'react';
import { 
  Container, Typography, FormControl, InputLabel, Select, MenuItem, 
  Button, Box, Card, CardContent, Tabs, Tab,
  Divider, Paper, List, ListItem, ListItemText, Switch,
  Chip, Alert, CircularProgress, Snackbar, FormHelperText
} from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../api/apiClient';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

export default function ManagePermissions() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [ownVessels, setOwnVessels] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [allVerifiers, setAllVerifiers] = useState([]);
  const [authorizedSuppliers, setAuthorizedSuppliers] = useState([]);
  const [authorizedVerifiers, setAuthorizedVerifiers] = useState([]);
  
  const [vesselImo, setVesselImo] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = suppliers, 1 = verifiers
  const [loadingVessels, setLoadingVessels] = useState(true);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [loadingVerifiers, setLoadingVerifiers] = useState(true);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [togglingSupplierId, setTogglingSupplierId] = useState(null);
  const [togglingVerifierId, setTogglingVerifierId] = useState(null);

  const [error, setError] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const fetchOwnVessels = async () => {
      setLoadingVessels(true);
      setError('');
      try {
        const response = await apiClient.get('/vessels');
        setOwnVessels(response.data.data || []);
      } catch (err) {
        console.error("Failed to fetch vessels:", err);
        if (err.response && err.response.data) {
          const p = err.response.data;
          setError(p.detail || p.title || 'Failed to load your vessels.');
        } else {
          setError('Failed to load your vessels.');
        }
      } finally {
        setLoadingVessels(false);
      }
    };
    if (user?.role === 'SHIPOWNER') {
        fetchOwnVessels();
    }
  }, [user]);

  useEffect(() => {
    const fetchAllSuppliers = async () => {
      setLoadingSuppliers(true);
      setError('');
      try {
        const response = await apiClient.get('/permissions/suppliers?per_page=100');
        const list = response.data.data || [];
        // Sort suppliers alphabetically by display name
        list.sort((a, b) => a.name.localeCompare(b.name));
        setAllSuppliers(list);
      } catch (err) {
        console.error("Failed to fetch suppliers:", err);
        if (err.response && err.response.data) {
          const p = err.response.data;
          setError(p.detail || p.title || 'Failed to load supplier list.');
        } else {
          setError('Failed to load supplier list.');
        }
      } finally {
        setLoadingSuppliers(false);
      }
    };

    const fetchAllVerifiers = async () => {
      setLoadingVerifiers(true);
      setError('');
      try {
        const response = await apiClient.get('/permissions/verifiers?per_page=100');
        const list = response.data.data || [];
        // Sort verifiers alphabetically by display name
        list.sort((a, b) => a.name.localeCompare(b.name));
        setAllVerifiers(list);
      } catch (err) {
        console.error("Failed to fetch verifiers:", err);
        if (err.response && err.response.data) {
          const p = err.response.data;
          setError(p.detail || p.title || 'Failed to load verifier list.');
        } else {
          setError('Failed to load verifier list.');
        }
      } finally {
        setLoadingVerifiers(false);
      }
    };

    if (user?.role === 'SHIPOWNER') {
      fetchAllSuppliers();
      fetchAllVerifiers();
    }
  }, [user]);

  useEffect(() => {
    if (!vesselImo) {
      setAuthorizedSuppliers([]);
      setAuthorizedVerifiers([]);
      return;
    }

    const fetchAuthorized = async () => {
      setLoadingPermissions(true);
      setError('');
      try {
        // Fetch authorized suppliers for selected vessel
        const supplierResponse = await apiClient.get(`/permissions/vessel/${vesselImo}/suppliers`);
        setAuthorizedSuppliers(
          (supplierResponse.data.data || []).map(p => p.supplier.id)
        );

        // Fetch authorized verifiers for selected vessel
        const verifierResponse = await apiClient.get(`/permissions/vessel/${vesselImo}/verifiers`);
        setAuthorizedVerifiers(
          (verifierResponse.data.data || []).map(p => p.verifier.id)
        );
      } catch (err) {
        console.error(`Failed to fetch permissions for ${vesselImo}:`, err);
        if (err.response && err.response.data) {
          const p = err.response.data;
          setError(p.detail || p.title || `Failed to load permissions for vessel ${vesselImo}.`);
        } else {
          setError(`Failed to load permissions for vessel ${vesselImo}.`);
        }
        setAuthorizedSuppliers([]);
        setAuthorizedVerifiers([]);
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchAuthorized();
  }, [vesselImo]);

  const handleVesselChange = (e) => {
    setVesselImo(e.target.value);
  };

  const handleSupplierToggle = async (supplierId, isCurrentlyChecked) => {
    if (!vesselImo || togglingSupplierId) return;

    const supplierUsername = allSuppliers.find(p => p.id === supplierId)?.username || 'this supplier';
    const action = isCurrentlyChecked ? 'Revoking' : 'Granting';
    const optimisticAuthorized = isCurrentlyChecked 
        ? authorizedSuppliers.filter(id => id !== supplierId)
        : [...authorizedSuppliers, supplierId];

    setTogglingSupplierId(supplierId);
    setAuthorizedSuppliers(optimisticAuthorized);
    setError('');

    try {
      if (isCurrentlyChecked) {
        // Revoke permission
        await apiClient.delete(`/permissions/vessel/${vesselImo}/suppliers/${supplierId}`);
        setSnackbarMessage(`Permission revoked for ${supplierUsername}.`);
      } else {
        // Grant permission
        await apiClient.post(`/permissions/vessel/${vesselImo}/suppliers`, { supplier_id: supplierId });
        setSnackbarMessage(`Permission granted to ${supplierUsername}.`);
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error(`${action} permission failed for ${supplierId} on ${vesselImo}:`, err);
      if (err.response && err.response.data) {
        const p = err.response.data;
        setError(p.detail || p.title || `${action} permission failed.`);
      } else {
        setError(`${action} permission failed.`);
      }
      setAuthorizedSuppliers(isCurrentlyChecked
          ? [...authorizedSuppliers, supplierId]
          : authorizedSuppliers.filter(id => id !== supplierId)
      );
    } finally {
      setTogglingSupplierId(null);
    }
  };

  const handleVerifierToggle = async (verifierId, isCurrentlyChecked) => {
    if (!vesselImo || togglingVerifierId) return;

    const verifierUsername = allVerifiers.find(p => p.id === verifierId)?.username || 'this verifier';
    const action = isCurrentlyChecked ? 'Revoking' : 'Granting';
    const optimisticAuthorized = isCurrentlyChecked 
        ? authorizedVerifiers.filter(id => id !== verifierId)
        : [...authorizedVerifiers, verifierId];

    setTogglingVerifierId(verifierId);
    setAuthorizedVerifiers(optimisticAuthorized);
    setError('');

    try {
      if (isCurrentlyChecked) {
        // Revoke permission
        await apiClient.delete(`/permissions/vessel/${vesselImo}/verifiers/${verifierId}`);
        setSnackbarMessage(`Permission revoked for ${verifierUsername}.`);
      } else {
        // Grant permission
        await apiClient.post(`/permissions/vessel/${vesselImo}/verifiers`, { verifier_id: verifierId });
        setSnackbarMessage(`Permission granted to ${verifierUsername}.`);
      }
      setSnackbarOpen(true);
    } catch (err) {
      console.error(`${action} permission failed for ${verifierId} on ${vesselImo}:`, err);
      if (err.response && err.response.data) {
        const p = err.response.data;
        setError(p.detail || p.title || `${action} permission failed.`);
      } else {
        setError(`${action} permission failed.`);
      }
      setAuthorizedVerifiers(isCurrentlyChecked
          ? [...authorizedVerifiers, verifierId]
          : authorizedVerifiers.filter(id => id !== verifierId)
      );
    } finally {
      setTogglingVerifierId(null);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  const selectedVesselName = ownVessels.find(v => v.imo === vesselImo)?.name || '';

  return (
    <div className="page-container">
      <Container>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/vessels')}
            sx={{ 
              mr: 2, 
              color: '#666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            Back to Vessels
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1565c0' }}>
            Manage Access Permissions
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Card className="card form-container" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 2, display: 'flex', alignItems: 'center' }}>
              <SecurityIcon sx={{ mr: 1, color: '#1565c0' }} />
              Select Vessel to Manage
            </Typography>
            
            {loadingVessels ? (
                <CircularProgress size={24} />
            ) : (
                <FormControl fullWidth variant="outlined" disabled={ownVessels.length === 0}>
                <InputLabel id="vessel-select-label">Vessel</InputLabel>
                <Select
                    labelId="vessel-select-label"
                    value={vesselImo}
                    label="Vessel"
                    onChange={handleVesselChange}
                >
                    <MenuItem value="" disabled><em>Select a vessel</em></MenuItem>
                    {ownVessels.map((v) => (
                    <MenuItem key={v.imo} value={v.imo}>
                        {v.imo} - {v.name}
                    </MenuItem>
                    ))}
                </Select>
                 {ownVessels.length === 0 && <FormHelperText>No vessels found.</FormHelperText>}
                </FormControl>
            )}
          </CardContent>
        </Card>
        
        {vesselImo && (
          <Card className="card" sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1, color: '#1565c0' }} />
                Access Permissions for {vesselImo} {selectedVesselName && `- ${selectedVesselName}`}
              </Typography>
              
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab 
                  icon={<PeopleIcon />} 
                  label={`Suppliers (${authorizedSuppliers.length}/${allSuppliers.length})`} 
                  iconPosition="start"
                />
                <Tab 
                  icon={<VerifiedIcon />} 
                  label={`Verifiers (${authorizedVerifiers.length}/${allVerifiers.length})`} 
                  iconPosition="start"
                />
              </Tabs>
              
              {/* Suppliers Tab */}
              {activeTab === 0 && (
                <>
                  {!loadingSuppliers && !loadingPermissions && (
                    <Chip 
                      label={`${authorizedSuppliers.length} of ${allSuppliers.length} Suppliers Authorized`}
                      size="small"
                      sx={{ bgcolor: 'rgba(21, 101, 192, 0.1)', color: '#1565c0', mb: 2 }}
                    />
                  )}
                  
                  {loadingSuppliers || loadingPermissions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : allSuppliers.length > 0 ? (
                    <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto', mb: 3 }}>
                      <List disablePadding>
                        {allSuppliers.map((p) => {
                          const isChecked = authorizedSuppliers.includes(p.id);
                          const isToggling = togglingSupplierId === p.id;
                          return (
                            <ListItem 
                              key={p.id}
                              divider
                              secondaryAction={
                                <Switch
                                  edge="end"
                                  checked={isChecked}
                                  onChange={() => handleSupplierToggle(p.id, isChecked)}
                                  color="primary"
                                  disabled={isToggling}
                                />
                              }
                            >
                              <ListItemText 
                                primary={p.name}
                                secondary={p.username}
                                primaryTypographyProps={{ fontWeight: isChecked ? 600 : 400 }}
                              />
                              {isToggling && <CircularProgress size={20} sx={{ ml: 1 }} />} 
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  ) : (
                    <Typography sx={{ textAlign: 'center', my: 3 }} color="textSecondary">
                      No supplier users found in the system.
                    </Typography>
                  )}
                </>
              )}
              
              {/* Verifiers Tab */}
              {activeTab === 1 && (
                <>
                  {!loadingVerifiers && !loadingPermissions && (
                    <Chip 
                      label={`${authorizedVerifiers.length} of ${allVerifiers.length} Verifiers Authorized`}
                      size="small"
                      sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)', color: '#ff9800', mb: 2 }}
                    />
                  )}
                  
                  {loadingVerifiers || loadingPermissions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : allVerifiers.length > 0 ? (
                    <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto', mb: 3 }}>
                      <List disablePadding>
                        {allVerifiers.map((p) => {
                          const isChecked = authorizedVerifiers.includes(p.id);
                          const isToggling = togglingVerifierId === p.id;
                          return (
                            <ListItem 
                              key={p.id}
                              divider
                              secondaryAction={
                                <Switch
                                  edge="end"
                                  checked={isChecked}
                                  onChange={() => handleVerifierToggle(p.id, isChecked)}
                                  color="primary"
                                  disabled={isToggling}
                                />
                              }
                            >
                              <ListItemText 
                                primary={p.name}
                                secondary={p.username}
                                primaryTypographyProps={{ fontWeight: isChecked ? 600 : 400 }}
                              />
                              {isToggling && <CircularProgress size={20} sx={{ ml: 1 }} />} 
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  ) : (
                    <Typography sx={{ textAlign: 'center', my: 3 }} color="textSecondary">
                      No verifier users found in the system.
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
              {snackbarMessage}
          </Alert>
      </Snackbar>
    </div>
  );
}
